import { RootState } from "src/redux/store";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Button, Flex, Text,
} from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { IconChevronRight } from "@tabler/icons-react";
import { ScreenLoader } from "src/components/screenLoader";
import { useDisclosure } from "@mantine/hooks";
import { usePrevious } from "src/hooks";
import { ScreenWrapper } from "../../components"
import { OnboardingProgress } from '../../components/onboardingProgress';
import { getProgressStepper } from '../../utils/helpers';
import useStyle from "./documentsUpload.style";
import ReVerificationButton from "./containers/reVerificationButton";
import { uploadDocument } from "./redux/modificationSlice";
import IncomeProofModal from "./containers/incomeProofModal";

type IDocument = "pan_image_upload" | "signature" | "income_proof"

export type IDocumentsToUpload = IDocument[] | []
interface DocumentsUploadProps {
  documentsToUpload: IDocumentsToUpload;
}

const DocumentsUpload = ({ documentsToUpload }:DocumentsUploadProps) => {
  const dispatch = useDispatch();
  const { currentStep, name } = useSelector((state: RootState) => state.account.userDetails);
  const [panFile, setPanFile] = useState<File | null>(null);
  const [signFile, setSignFile] = useState<File | null>(null);
  const [bankFile, setBankFile] = useState<File | null>(null);
  const { classes } = useStyle();
  const [userName, setUserName] = useState(name || '');
  const buttonDisabled = !panFile || !signFile || !bankFile;
  const {
    data, loading, isError, status,
  } = useSelector((state: RootState) => state.modification.uploadDocument);
  const [opened, { open, close }] = useDisclosure(false);
  const [error, setError] = useState({
    pan_image_upload: false,
    signature: false,
    income_proof: false,
  })
  const resetRef = useRef<() => void>(null);
  const prevDocumentToUpload = usePrevious(documentsToUpload);

  const documentsUploadData = [
    {
      title: 'PAN',
      description: 'Upload your PAN card',
      file: panFile,
      setFile: setPanFile,
      key: 'pan_image_upload',
      accept: 'image/*',
      isDirectUpload: true,
    },
    {
      title: 'Signature',
      description: 'Upload your signature',
      file: signFile,
      setFile: setSignFile,
      key: 'signature',
      accept: 'image/*',
      isDirectUpload: true,
    },
    {
      title: 'Income Proof',
      description: 'Upload Income Proof',
      file: bankFile,
      setFile: setBankFile,
      key: 'income_proof',
      accept: '.pdf',
      ref: resetRef,
      isDirectUpload: false,
      handler: () => open(),
    },
  ]
  useEffect(() => {
    setUserName(name)
  }, [name])

  useEffect(() => {
    if (isError) {
      if (data.step === 'pan_image_upload') {
        setError({ ...error, pan_image_upload: true })
        setPanFile(null)
      }
      if (data.step === 'signature') {
        setError({ ...error, signature: true })
        setSignFile(null)
      }
      if (data.step === 'income_proof') {
        setError({ ...error, income_proof: true })
        setBankFile(null)
      }
    }
  }, [data, status, isError])

  const uploadSingleDocument = (file: File | null, key:string) => {
    if (key === 'income_proof') {
      open()
    } else dispatch(uploadDocument({ data: file, step: key }))
  }

  const handleUploadIncomeProof = (docType: string, docPassword: string) => {
    dispatch(uploadDocument({
      data: bankFile, step: "income_proof", document_type: docType, password: docPassword,
    }))
  }

  const handleCloseModal = () => {
    resetRef.current?.();
    setBankFile(null)
    close()
  }

  // to close income proof modal, after upload
  useEffect(() => {
    if (status === 200) {
      if (prevDocumentToUpload.includes('income_proof' as never)
      && !documentsToUpload.includes('income_proof' as never)) {
        close()
      }
    }
  }, [documentsToUpload, status])

  return (
    <ScreenWrapper>
      <Box className="csMainContainer">
        <Box className="csHeader">
          <Box className="csProgressWrapper">
            <OnboardingProgress progress={getProgressStepper(currentStep)} />
          </Box>
        </Box>
        <Box className={classes.contentContainer}>
          <h2 className="csHeading">
            Welcome Back,
            {' '}
            {`${userName}`}
          </h2>
          <Text className="content_sm_regular text_fg_muted">
            Let’s finish your verification process
          </Text>
          <Text my="1.5rem" className="content_xs_regular text_fg_subtle">
            Please re-upload the following documents to complete your verification
          </Text>

          <Flex direction="column" gap="md">
            {documentsUploadData.map(({
              title, file, setFile, key, accept, ref, isDirectUpload, handler,
              // @ts-ignore
            }) => documentsToUpload.includes(key as IDocument) && (
              <ReVerificationButton
                key={key}
                setFileState={setFile}
                fileState={file}
                uploadText={`Upload ${title}`}
                uploadedText={`${title} Uploaded`}
                onUpload={() => uploadSingleDocument(file, key)}
                accept={accept}
                // @ts-ignore
                isError={error[key]}
                resetRef={ref}
                isDirectUpload={isDirectUpload}
                uploadModal={handler}
              />
            ))}
          </Flex>
        </Box>
        <Box className="csFooter">
          <Button
            disabled={buttonDisabled}
            className="heading_2xs_semibold text_base_white"
            type="submit"
            onClick={() => {}}
            radius="md"
            rightIcon={<IconChevronRight />}
          >
            Done
          </Button>
        </Box>
      </Box>
      {loading && <ScreenLoader />}
      <IncomeProofModal
        opened={opened}
        onClose={close}
        file={bankFile}
        setFile={setBankFile}
        handleDiscard={handleCloseModal}
        handleSave={handleUploadIncomeProof}
      />
    </ScreenWrapper>
  )
}

export default DocumentsUpload
